/**
 * Payabli's check scanner (remote deposit capture) opens its camera in a
 * `.rdc-modal-container` it appends to `document.body` — outside the React
 * tree, and so outside the Radix dialog that hosts the scan form. Two things
 * then break a real user, and neither shows in a unit test of the form:
 *
 *   - Radix sets `pointer-events: none` on `body` while a modal layer is open,
 *     and the camera modal inherits it: the shutter cannot be clicked.
 *   - A click that does land counts as an interaction OUTSIDE the dialog, so
 *     Radix dismisses the dialog and orphans the camera over an empty page.
 *
 * The CSS half lives in `index.css` (`.rdc-modal-container` re-enables pointer
 * events). This half reclassifies the camera modal as part of the layer that
 * opened it, the same way `withPlacesDropdownGuard` does for Places.
 *
 * Radix's focus trap still pulls keyboard focus back into the dialog: a mouse
 * or touch press on the camera controls goes through (a click is delivered to
 * what is under the pointer, whatever holds focus), keyboard-only use of the
 * camera does not.
 */
export const PAYABLI_CHECK_CAPTURE_MODAL_SELECTOR = '.rdc-modal-container';

/** True when `target` is inside Payabli's check-capture camera modal. */
export function isPayabliCheckCaptureTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(PAYABLI_CHECK_CAPTURE_MODAL_SELECTOR));
}

type OutsideEvent = {
  detail: { originalEvent: { target: EventTarget | null } };
  preventDefault: () => void;
};

/** Wraps a Radix outside-interaction / focus-outside handler so the camera
 *  modal never dismisses the layer; everything else falls through. */
export function withPayabliCheckCaptureGuard<Event extends OutsideEvent>(
  handler: ((event: Event) => void) | undefined,
): (event: Event) => void {
  return (event: Event) => {
    if (isPayabliCheckCaptureTarget(event.detail.originalEvent.target)) {
      event.preventDefault();
      return;
    }
    handler?.(event);
  };
}
